/** SUNTRONIX ONE — ONE Toolbox.
 *  Role-aware, searchable directory of every tool the user is authorised for.
 *  RBAC is enforced server-side; a tool you cannot access is never returned.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Search, Pin, Star, Clock, ArrowRight, Loader2, Sparkles,
  FileText, Users, Package, ClipboardList, Boxes, ShieldCheck, Apple,
  Receipt, Mail, MessageCircle, BookOpen, Settings2, Building2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const ICONS = {
  FileText, Users, Package, ClipboardList, Boxes, ShieldCheck, Apple,
  Receipt, Mail, MessageCircle, BookOpen, Sparkles, Settings2, Building2,
  LayoutGrid,
};
const STATUS_TONE = {
  READY: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "SETUP REQUIRED": "bg-violet-100 text-violet-800 border-violet-200",
  RESTRICTED: "bg-amber-100 text-amber-800 border-amber-200",
};

function ToolIcon({ name, className }) {
  const Ico = ICONS[name] || LayoutGrid;
  return <Ico className={className} />;
}

function ToolCard({ tool, onOpen, onPin, onFav }) {
  return (
    <div
      data-testid={`toolbox-card-${tool.id}`}
      onClick={() => onOpen(tool)}
      className="group relative flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 cursor-pointer transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-300"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
          <ToolIcon name={tool.icon} className="h-5 w-5" />
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid={`toolbox-fav-${tool.id}`}
            onClick={(e) => { e.stopPropagation(); onFav(tool); }}
            title={tool.favourite ? "Remove favourite" : "Add favourite"}
            className="p-1 rounded-md text-slate-300 hover:text-amber-500 transition-colors"
          >
            <Star className={`h-4 w-4 ${tool.favourite ? "fill-amber-400 text-amber-400" : ""}`} />
          </button>
          <button
            data-testid={`toolbox-pin-${tool.id}`}
            onClick={(e) => { e.stopPropagation(); onPin(tool); }}
            title={tool.pinned ? "Unpin" : "Pin to top"}
            className="p-1 rounded-md text-slate-300 hover:text-indigo-600 transition-colors"
          >
            <Pin className={`h-4 w-4 ${tool.pinned ? "fill-indigo-500 text-indigo-500" : ""}`} />
          </button>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-semibold text-slate-900">{tool.name}</h3>
          {tool.status !== "READY" && (
            <Badge className={`text-[10px] border ${STATUS_TONE[tool.status] || ""}`}>{tool.status}</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{tool.desc}</p>
      </div>
      <div className="mt-auto flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{tool.category}</span>
        <span className="flex items-center text-[12px] font-medium text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
          Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </span>
      </div>
    </div>
  );
}

export default function OneToolbox() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query) => {
    try {
      const { data } = await api.get("/toolbox/tools", { params: query ? { q: query } : {} });
      setData(data);
    } catch {
      toast.error("Could not load your toolbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const open = useCallback((tool) => {
    api.post("/toolbox/recent", { tool_id: tool.id }).catch(() => {});
    nav(tool.route);
  }, [nav]);

  const toggle = useCallback(async (endpoint, tool) => {
    try {
      await api.post(`/toolbox/${endpoint}`, { tool_id: tool.id });
      await load(q.trim());
    } catch {
      toast.error("Could not update");
    }
  }, [load, q]);

  const onPin = useCallback((t) => toggle("pin", t), [toggle]);
  const onFav = useCallback((t) => toggle("favourite", t), [toggle]);

  const cardProps = useMemo(() => ({ onOpen: open, onPin, onFav }), [open, onPin, onFav]);

  const allTools = useMemo(
    () => (data?.categories || []).flatMap((c) => c.tools),
    [data]
  );
  const categoryNames = useMemo(
    () => ["All", ...(data?.categories || []).map((c) => c.name)],
    [data]
  );
  const visibleTools = useMemo(
    () => (cat === "All" ? allTools : allTools.filter((t) => t.category === cat)),
    [allTools, cat]
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="one-toolbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-indigo-600 font-semibold">SUNTRONIX ONE</div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <LayoutGrid className="h-6 w-6 text-indigo-600" /> ONE Toolbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">Every tool you're authorised to use, in one place. Pin the ones you rely on.</p>
        </div>
        {data && <Badge className="bg-slate-100 text-slate-700" data-testid="toolbox-total">{data.total} tools available</Badge>}
      </div>

      <div className="relative mt-5 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="toolbox-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tools by name, category or what they do…"
          className="pl-9 h-11"
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 mt-10" data-testid="toolbox-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your toolbox…
        </div>
      )}

      {data && !loading && (
        <div className="mt-6 space-y-8">
          {!q && data.pinned?.length > 0 && (
            <section data-testid="toolbox-pinned-section">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                <Pin className="h-4 w-4 text-indigo-600" /> Pinned
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.pinned.map((t) => <ToolCard key={t.id} tool={t} {...cardProps} />)}
              </div>
            </section>
          )}

          {!q && data.recent?.length > 0 && (
            <section data-testid="toolbox-recent-section">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                <Clock className="h-4 w-4 text-slate-500" /> Recently used
              </h2>
              <div className="flex flex-wrap gap-2">
                {data.recent.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`toolbox-recent-${t.id}`}
                    onClick={() => open(t)}
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-slate-300 hover:shadow-sm transition-[box-shadow,border-color]"
                  >
                    <ToolIcon name={t.icon} className="h-3.5 w-3.5 text-slate-500" /> {t.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* When searching, show a single flat result grid across all categories */}
          {q ? (
            <section data-testid="toolbox-search-results">
              {allTools.length === 0 ? (
                <div className="text-center text-slate-500 py-16" data-testid="toolbox-empty">
                  No tools match "{q}". Try a different search.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {allTools.map((t) => <ToolCard key={t.id} tool={t} {...cardProps} />)}
                </div>
              )}
            </section>
          ) : (
            <section data-testid="toolbox-all-section">
              <div className="flex flex-wrap gap-2 mb-4" data-testid="toolbox-cat-filters">
                {categoryNames.map((c) => (
                  <button
                    key={c}
                    data-testid={`toolbox-cat-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setCat(c)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
                      cat === c
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visibleTools.map((t) => <ToolCard key={t.id} tool={t} {...cardProps} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
